import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  BreadcrumbGroup,
  Button,
  ContentLayout,
  Flashbar,
  FlashbarProps,
  Header,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { BulkLeaveWaitlistCommandOutput } from '@amzn/red-velvet-api';
import { ActivitySidebar } from '../myActivity/sideNav';
import { WaitlistInfoHelpPanel } from '../myActivity/waitlistInfoHelpPanel';
import { getValueFromSearchParam } from '../../utils/url';
import { useGetAllWaitlistsByEvent } from '../../hooks/waitlist/useGetAllWaitlistsByEvent';
import { isDisplayableWaitlistEntry } from '../../utils/waitlistUtils';
import { WaitlistEventCard } from './waitlistEventCard';
import { WaitlistTable } from './waitlistTable';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { SHIFT_WAITLIST } from '../../utils/constants';
import { isUnlistedEvent } from '../../utils/eventUtils';
import { publishKatalMetric } from '../katalAnalytics';
import { COORDINATOR_WAITLIST_PAGE_VIEW } from '../../utils/constants';

export function CoordinatorManageWaitlistPage() {
  const { t } = useTranslation(['coordinatorManageWaitlist', 'activity', 'translation']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlFeatures = searchParams.get('features');
  const features = useFeatures();
  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const isWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  const { data, isError, error, refetch, isLoading } = useGetAllWaitlistsByEvent(eventId);
  const event = data?.event;
  const displayEntries = (data?.waitlistEntries ?? []).filter(isDisplayableWaitlistEntry);
  const coordinatorActivityUrl = `/coordinator-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;

  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);

  // Gate the page-view on the feature flag: React runs effects even when the component renders
  // null (flag off), so without this guard navigating here with the flag off would still inflate
  // the metric during limited rollout.
  useEffect(() => {
    if (isWaitlistEnabled) {
      publishKatalMetric(COORDINATOR_WAITLIST_PAGE_VIEW, `eventId=${eventId}`);
    }
  }, [eventId, isWaitlistEnabled]);

  const dismissFlashItem = useCallback(
    (id: string) => setFlashItems((prev) => prev.filter((item) => item.id !== id)),
    []
  );

  const handleRemoveComplete = useCallback(
    (result: BulkLeaveWaitlistCommandOutput) => {
      const items: FlashbarProps.MessageDefinition[] = [];
      const succeededCount = result.succeededDeletedWaitlistEntries?.length ?? 0;
      const failed = result.failedDeletedWaitlistEntries ?? [];

      if (succeededCount > 0) {
        items.push({
          type: 'success',
          dismissible: true,
          onDismiss: () => dismissFlashItem('bulk-remove-success'),
          content: t('bulkRemoveAlerts.successMessage', { count: succeededCount }),
          id: 'bulk-remove-success',
        });
      }

      if (failed.length > 0) {
        const aliasById = new Map(
          (data?.waitlistEntries ?? []).map((entry) => [entry.waitlistEntryId, entry.alias])
        );
        const aliases = failed
          .map((f) => aliasById.get(f.waitlistEntryId))
          .filter(Boolean)
          .join(', ');
        items.push({
          type: 'error',
          dismissible: true,
          onDismiss: () => dismissFlashItem('bulk-remove-failure'),
          content: t('bulkRemoveAlerts.failureMessage', { count: failed.length }),
          action: (
            <Button onClick={() => navigator.clipboard?.writeText(aliases)?.catch(() => undefined)}>
              {t('bulkRemoveAlerts.copyFailedAliases')}
            </Button>
          ),
          id: 'bulk-remove-failure',
        });
      }

      setFlashItems(items);
      refetch();
    },
    [t, data?.waitlistEntries, dismissFlashItem, refetch]
  );

  if (!isWaitlistEnabled) {
    return null;
  }
  function renderWaitlistTableContent() {
    if (isError) {
      return (
        <Flashbar
          items={[
            {
              type: 'error',
              header: t('apiError.header'),
              content: error?.message || t('apiError.content'),
            },
          ]}
        />
      );
    }

    if (event && isUnlistedEvent(event)) {
      return (
        <Flashbar
          items={[
            {
              type: 'warning',
              content: t('unsupportedEvent.content'),
            },
          ]}
        />
      );
    }

    return (
      <WaitlistTable
        entries={displayEntries}
        shifts={data?.shifts ?? []}
        isLoading={isLoading}
        onBulkRemoveComplete={handleRemoveComplete}
      />
    );
  }

  return (
    <AppLayout
      footerSelector=".footer"
      navigation={<ActivitySidebar baseUrl={coordinatorActivityUrl} />}
      toolsHide={false}
      toolsOpen={infoPanelOpen}
      onToolsChange={({ detail }) => setInfoPanelOpen(detail.open)}
      tools={<WaitlistInfoHelpPanel />}
      contentType="table"
      notifications={
        flashItems.length > 0 ? (
          <Flashbar data-testid="waitlist-flashbar" items={flashItems} />
        ) : undefined
      }
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            {
              text: t('breadcrumbs.home', { ns: 'translation' }),
              href: `/${urlFeatures ? '?features=' + urlFeatures : ''}`,
            },
            {
              text: t('breadcrumbs.coordinatorActivity', { ns: 'activity' }),
              href: coordinatorActivityUrl,
            },
            { text: t('breadcrumbs.manageWaitlist'), href: '#' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />
      }
      content={
        <ContentLayout
          header={
            event && (
              <Header
                variant="h1"
                info={
                  <Link
                    variant="info"
                    data-testid="manageWaitlist.infoLink"
                    data-aci-analytics-name="Waitlist_infoPanelOpened"
                    onFollow={(e) => {
                      e.preventDefault();
                      setInfoPanelOpen(true);
                    }}
                  >
                    {t('waitlistsTab.info.linkLabel', { ns: 'activity', defaultValue: 'Info' })}
                  </Link>
                }
              >
                {t('header.title', { eventTitle: event.title })}
              </Header>
            )
          }
        >
          <SpaceBetween direction="vertical" size="l">
            {event && <WaitlistEventCard event={event} />}
            {renderWaitlistTableContent()}
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
}
